import React from 'react';
import OutlinedButton from '@/components/common/buttons/OutlinedButton';
import { Button, Dialog, HStack, Portal, Text } from '@chakra-ui/react';
import { CONTROL_SURFACE_SHADOW } from '@/components/ui/shadows';

type ConfirmButtonPalette = 'brand' | 'red' | 'green';

interface ConfirmationDialogProps {
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	title: string;
	message?: string;
	confirmText?: string;
	cancelText?: string;
	onConfirm: () => void;
	onCancel: () => void;
	isLoading?: boolean;
	loadingText?: string;
	children?: React.ReactNode;
	confirmIcon?: React.ReactNode;
	confirmButtonVariant?: 'solid' | 'outline' | 'ghost' | 'subtle';
	confirmButtonColorPalette?: ConfirmButtonPalette;
	disabled?: boolean;
}

const PALETTE_OVERRIDES: Partial<Record<ConfirmButtonPalette, { bg: string; color: string; _hover: { bg: string } }>> =
	{
		red: { bg: 'red.500', color: 'white', _hover: { bg: 'red.600' } },
		green: { bg: 'green.500', color: 'white', _hover: { bg: 'green.600' } },
	};

const DESTRUCTIVE_FOCUS_VISIBLE = {
	outline: '1px solid var(--chakra-colors-red-500)',
	outlineOffset: '2px',
} as const;

/**
 * Generic reusable confirmation dialog component
 */
const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
	isOpen,
	onOpenChange,
	title,
	message,
	confirmText = 'Confirm',
	cancelText = 'Cancel',
	onConfirm,
	onCancel,
	isLoading = false,
	loadingText = 'Processing...',
	children,
	confirmIcon,
	confirmButtonVariant = 'solid',
	confirmButtonColorPalette = 'brand',
	disabled = false,
}) => {
	const contentRef = React.useRef<HTMLDivElement | null>(null);
	const confirmVisual =
		confirmButtonColorPalette === 'red'
			? 'danger'
			: confirmButtonColorPalette === 'green'
				? 'secondary'
				: 'primary';
	const overrides = PALETTE_OVERRIDES[confirmButtonColorPalette];

	return (
		<Dialog.Root
			size="lg"
			placement="center"
			motionPreset="slide-in-bottom"
			open={isOpen}
			onOpenChange={({ open }) => onOpenChange(open)}
			closeOnInteractOutside={!isLoading}
			closeOnEscape={!isLoading}
			initialFocusEl={
				confirmButtonColorPalette === 'red'
					? () => contentRef.current?.querySelector<HTMLButtonElement>('[data-cancel-action="true"]') ?? null
					: undefined
			}
		>
			<Portal>
				<Dialog.Backdrop />
				<Dialog.Positioner>
					<Dialog.Content ref={contentRef} maxW="440px" borderRadius="3xl" boxShadow="md">
						<Dialog.Header pb={2}>
							<Dialog.Title>{title}</Dialog.Title>
						</Dialog.Header>

						<Dialog.Body p={6} pt={0}>
							{message && (
								<Text textStyle="body" color="fg.muted" whiteSpace="pre-line">
									{message}
								</Text>
							)}
							{children}
						</Dialog.Body>

						<Dialog.Footer>
							{/* TODO: Revisit desktop dialog action sizing in a broader button-system pass. */}
							<HStack gap={3} w="100%" justify="flex-end">
								<OutlinedButton
									data-cancel-action="true"
									onClick={onCancel}
									disabled={isLoading}
									minW={28}
									elevated
								>
									{cancelText}
								</OutlinedButton>
								<Button
									visual={confirmVisual}
									onClick={onConfirm}
									loading={isLoading}
									loadingText={loadingText}
									variant={overrides ? undefined : confirmButtonVariant}
									bg={overrides?.bg}
									color={overrides?.color}
									textStyle="label"
									boxShadow={CONTROL_SURFACE_SHADOW}
									_hover={overrides?._hover}
									_focusVisible={
										confirmButtonColorPalette === 'red' ? DESTRUCTIVE_FOCUS_VISIBLE : undefined
									}
									width="auto"
									minW={28}
									disabled={disabled || isLoading}
								>
									{confirmIcon}
									{confirmText}
								</Button>
							</HStack>
						</Dialog.Footer>
					</Dialog.Content>
				</Dialog.Positioner>
			</Portal>
		</Dialog.Root>
	);
};

export default ConfirmationDialog;
